import React from "react";
import { Link } from "react-router-dom";
import { Scale, Minus, Plus, Equal } from "lucide-react";
import { money, moneySigned, Panel, TrendChip, InlineError } from "./singleUtils";

const Line = ({ label, value, op, cls = "text-foreground", testid, hint }) => (
  <div className="flex items-center justify-between py-[5px]" data-testid={testid} title={hint}>
    <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
      {op === "-" && <Minus className="w-3 h-3 text-rose-500/70" />}
      {op === "+" && <Plus className="w-3 h-3 text-emerald-500/70" />}
      {op === "=" && <Equal className="w-3 h-3 text-muted-foreground/60" />}
      {label}
    </span>
    <span className={`text-sm font-bold font-mono ${cls}`}>{value}</span>
  </div>
);

// Financial Performance — the profitability waterfall.
// Operating Result == Net Profit (job basis, completed jobs only):
// job revenue − commissions − parts + processing fees charged to techs.
// Matches Financial Reports → General. Tax is NEVER treated as revenue.
const ProfitPanel = ({ financial, onRetry }) => {
  const f = financial;
  if (!f) return null;
  if (f.error) return <Panel title="Financial Performance" icon={Scale} testid="profitability-waterfall-card"><InlineError label="financial data" onRetry={onRetry} /></Panel>;
  const op = f.operating_result;
  const opCls = (op || 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-500";
  return (
    <Panel title="Financial Performance" icon={Scale} testid="profitability-waterfall-card"
      right={<Link to="/dashboard/financials" className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:underline">Full report →</Link>}>
      <div className="divide-y divide-border/40">
        <Line label="Money Collected" value={money(f.collected)} testid="fin-collected" hint="Cash actually received in this period" />
        <Line label="Tax Collected" op="-" value={money(f.tax_collected)} cls="text-muted-foreground" testid="fin-tax" />
        <Line label="Revenue Before Tax (collected)" op="=" value={money(f.collected_before_tax)} testid="fin-rbt" />
      </div>
      <p className="mt-2 mb-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">Completed Jobs — Profit</p>
      <div className="divide-y divide-border/40">
        <Line label="Job Revenue (pre-tax)" value={money(f.job_revenue)} testid="fin-job-revenue" hint="Full pre-tax revenue of jobs completed in this period (pending-payment jobs excluded)" />
        <Line label="Technician Commissions" op="-" value={money(f.tech_commissions)} cls="text-rose-600 dark:text-rose-500" testid="fin-commissions" />
        <Line label="Parts Cost" op="-" value={money(f.parts_cost)} cls="text-rose-600 dark:text-rose-500" testid="fin-parts" />
        <Line label="Processing Fees (charged to techs)" op="+" value={money(f.processing_fees)} cls="text-emerald-600 dark:text-emerald-400" testid="fin-processing-fees" hint="Card fees passed onto technicians — recovered by the company" />
      </div>
      <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/[0.05] px-3 py-2.5" data-testid="fin-operating-result">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-400">Operating Result</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Net profit on completed jobs — matches the Full Report</p>
          </div>
          <div className="text-right">
            <p className={`text-xl font-extrabold font-mono ${opCls}`}>{op === null || op === undefined ? "—" : moneySigned(op)}</p>
            <TrendChip pct={f.operating_trend} />
          </div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 text-[11px]">
        <div className="flex items-center justify-between py-1">
          <span className="text-muted-foreground">Outstanding</span>
          <span className="font-mono font-semibold text-orange-500">{money(f.outstanding)}</span>
        </div>
        <div className="flex items-center justify-between py-1">
          <span className="text-muted-foreground">Deposits Held</span>
          <span className="font-mono font-semibold text-teal-600 dark:text-teal-400">{money(f.deposits_held)}</span>
        </div>
        <div className="flex items-center justify-between py-1">
          <span className="text-muted-foreground">Invoice Sales (issued)</span>
          <span className="font-mono font-semibold text-foreground">{money(f.invoice_sales)}</span>
        </div>
        <div className="flex items-center justify-between py-1" title="Business expenses recorded in this period (tracked separately — not part of job profit)">
          <span className="text-muted-foreground">Expenses</span>
          <span className="font-mono font-semibold text-rose-600 dark:text-rose-500" data-testid="fin-expenses">{f.expenses_total === null || f.expenses_total === undefined ? "—" : money(f.expenses_total)}</span>
        </div>
        <div className="col-span-2 flex items-center justify-between py-1.5 mt-0.5 border-t border-border/40" title="Operating Result minus business expenses recorded in this period" data-testid="fin-net-after-expenses">
          <span className="text-muted-foreground font-medium">Net Profit After Expenses</span>
          <span className={`font-mono font-bold ${(op || 0) - (f.expenses_total || 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-500"}`}>
            {op === null || op === undefined ? "—" : moneySigned(Math.round(((op || 0) - (f.expenses_total || 0)) * 100) / 100)}
          </span>
        </div>
      </div>
    </Panel>
  );
};

export default ProfitPanel;
